import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoginService } from '@app/common/login.service';

// Fenêtre pendant laquelle l'accès aux données sensibles reste ouvert après saisie du mot de
// passe. Courte volontairement : la donnée est sensible, et la VRAIE barrière est côté serveur
// (GET /hr/employees ne renvoie déjà pas le salaire aux non-administrateurs).
export const SENSITIVE_TTL_MS = 5 * 60_000;		// 5 minutes

/**
 * Déverrouillage TEMPORAIRE des données RH sensibles (salaires…). L'accès exige de RETAPER le
 * mot de passe (vérifié côté serveur via LoginService.verifyPassword$) ; il se re-verrouille de
 * lui-même après SENSITIVE_TTL_MS. Partagé (providedIn root) : déverrouiller une fiche évite de
 * re-saisir sur la suivante dans la fenêtre.
 *
 * ⚠️ Ce service ne fait que l'ERGONOMIE du « retaper le mot de passe » — il ne protège rien à lui
 * seul (la donnée n'atteint le navigateur que si le serveur l'a jugée autorisée). La sécurité
 * réelle vit dans hr/routes.rs (lecture) et hr/model.rs (écriture).
 *
 * ⚠️⚠️ ET AUJOURD'HUI LE MOT DE PASSE N'EST PAS VÉRIFIÉ DU TOUT. `user_authenticate`
 * (common/src/user/model.rs) sélectionne le login puis émet un jeton SANS JAMAIS comparer la
 * colonne `password` : n'importe quelle saisie déverrouille (vérifié le 2026-07-27 sur
 * mercalinktest). Ce geste est donc, en l'état, une simple confirmation d'intention — surtout pas
 * une preuve d'identité. Faille de PLATEFORME, hors du périmètre de cette branche, laissée ouverte
 * comme le secret JWT `b"secret"` de la même fonction : voir audit-qualite-rh-2026-07-27.md §9.
 */
@Injectable({ providedIn: 'root' })
export class SensitiveAccessService {
	private login = inject(LoginService);
	private timer: ReturnType<typeof setTimeout> | null = null;

	// Signal lu par les templates : bascule seul à false à l'échéance du TTL.
	public readonly unlocked = signal(false);

	constructor() {
		// Verrouille à TOUT changement d'identité — déconnexion (user$ émet undefined) comme
		// connexion d'un autre utilisateur. Sans cela, le service (providedIn root) survivrait à un
		// logout SPA : un second habilité connecté sur le même onglet dans la fenêtre du TTL verrait
		// les données sensibles sans retaper SON mot de passe.
		let prev = this.login.loggeduser?.id;
		this.login.user$.subscribe(u => {
			if (u?.id !== prev) { prev = u?.id; this.lock(); }
		});
	}

	// Vérifie le mot de passe courant puis, s'il est bon, ouvre l'accès pour SENSITIVE_TTL_MS.
	public unlock$(password: string): Observable<boolean> {
		return this.login.verifyPassword$(password).pipe(
			tap(ok => { if (ok) this.open(); }),
		);
	}

	// Re-verrouille immédiatement (bouton « Masquer », déconnexion, changement d'utilisateur).
	public lock(): void {
		if (this.timer) { clearTimeout(this.timer); this.timer = null; }
		this.unlocked.set(false);
	}

	private open(): void {
		if (this.timer) clearTimeout(this.timer);
		this.unlocked.set(true);
		this.timer = setTimeout(() => this.lock(), SENSITIVE_TTL_MS);
	}
}
